// src/shared/components/Modals/EditDeliveryRequestModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { updateDeliveryFn } from "../../../firebase/deliveryFunctions";
import { getTransportIcon } from "../../../utils/transportIcons";

const msToLocal = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};
const localToMs = (str) => (str ? new Date(str).getTime() : null);

// A courier is "locked in" once someone has taken the job. Adjust the field
// names here if your delivery doc uses something other than courierId/driverId.
export const isCourierAssigned = (d) =>
  Boolean(d?.courierId) ||
  ["ongoing", "accepted", "picked"].includes(d?.status);

// Sender may edit while the job is still live.
export const canSenderEdit = (d) =>
  d?.status !== "completed" && d?.status !== "cancelled" && d?.deliveryTransportType !== "eco-bus" && d?.deliveryTransportType !== "bus-transport";

export function EditDeliveryRequestModal({ delivery, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!delivery) return;
    setValues({
      origin: delivery.originInfo?.address ?? delivery.origin ?? "",
      destination:
        delivery.destinationInfo?.address ?? delivery.destination ?? "",
      pickupTime: msToLocal(delivery.pickupTime ?? delivery.pickUpTime),
      targetTime: msToLocal(delivery.targetTime),
      originInstructions: delivery.originInfo?.details?.instructions ?? "",
      destinationInstructions:
        delivery.destinationInfo?.details?.instructions ?? "",
    });
    setError(null);
  }, [delivery, open]);

  const set = (field) => (e) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const addressChanged =
    values.origin !==
      (delivery?.originInfo?.address ?? delivery?.origin ?? "") ||
    values.destination !==
      (delivery?.destinationInfo?.address ?? delivery?.destination ?? "");

  const handleSave = async () => {
    const pickupMs = localToMs(values.pickupTime);
    const targetMs = localToMs(values.targetTime);

    if (!values.origin?.trim() || !values.destination?.trim()) {
      setError(t("delivery.errors.selectAddresses"));
      return;
    }
    if (pickupMs && targetMs && targetMs <= pickupMs) {
      setError(
        `${t("deliveryTargetTime")} > ${t("deliveryPickUpTime")}`
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateDeliveryFn(delivery.id ?? delivery.deliveryId, {
        origin: values.origin,
        destination: values.destination,
        pickupTime: pickupMs,
        targetTime: targetMs,
        originInfo: {
          ...(delivery.originInfo ?? {}),
          address: values.origin,
          details: {
            ...(delivery.originInfo?.details ?? {}),
            instructions: values.originInstructions,
          },
        },
        destinationInfo: {
          ...(delivery.destinationInfo ?? {}),
          address: values.destination,
          details: {
            ...(delivery.destinationInfo?.details ?? {}),
            instructions: values.destinationInstructions,
          },
        },
      });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Update failed:", err);
      setError(err.message || t("delivery.errors.createFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!delivery) return null;

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getTransportIcon(delivery.deliveryTransportType, 18)}
          {t("delivery.editDetailsDescription")}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isCourierAssigned(delivery) && (
            <Alert severity="warning">
              {t("delivery.status.ongoing")}. Reitin tai aikojen muuttaminen voi
              muuttaa hintaa, ja kuljetus laskutetaan toteutuneen matkan mukaan.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Label>{t("delivery.route")}</Label>
          <TextField
            label={t("delivery.origin")}
            value={values.origin ?? ""}
            onChange={set("origin")}
            size="small"
            fullWidth
          />
          <TextField
            label={t("delivery.destination")}
            value={values.destination ?? ""}
            onChange={set("destination")}
            size="small"
            fullWidth
          />
          {addressChanged && (
            <Typography variant="caption" color="text.secondary">
              Huom: osoitteen muokkaus ei päivitä karttakoordinaatteja. Isommat
              reittimuutokset kannattaa tehdä uutena tilauksena.
            </Typography>
          )}

          <Label>{t("delivery.addressDetails")}</Label>
          <TextField
            label={t("delivery.form.pickupInstructions")}
            placeholder={t("delivery.form.pickupInstructionsPlaceholder")}
            value={values.originInstructions ?? ""}
            onChange={set("originInstructions")}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label={t("delivery.form.deliveryInstructions")}
            placeholder={t("delivery.form.deliveryInstructionsPlaceholder")}
            value={values.destinationInstructions ?? ""}
            onChange={set("destinationInstructions")}
            size="small"
            fullWidth
            multiline
            rows={2}
          />

          <Label>{t("delivery.timing")}</Label>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label={t("deliveryPickUpTime")}
              type="datetime-local"
              value={values.pickupTime ?? ""}
              onChange={set("pickupTime")}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("deliveryTargetTime")}
              type="datetime-local"
              value={values.targetTime ?? ""}
              onChange={set("targetTime")}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {saving ? t("processing") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Label({ children }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.8, mt: 0.5, mb: -1 }}
    >
      {children}
    </Typography>
  );
}